import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box, CardContainer } from "@memorang/ui";
import { Text } from "react-native-paper";
import {
	type ProethicaScoreDelta,
	type ProethicaScoreDeltaDirection,
	buildProethicaScoreDelta,
} from "../../helpers/proethica-test-report";
import { ScoreDonut } from "./ScoreDonut";

type Props = {
	prePercentage: number;
	postPercentage: number;
};

const DeltaLine = ({ delta }: { delta: ProethicaScoreDelta }) => {
	const theme = useAppTheme();
	const deltaColorByDirection: Record<ProethicaScoreDeltaDirection, string> = {
		up: theme.colors.success.main,
		down: theme.colors.error,
		flat: theme.colors.onSurfaceVariant,
	};

	return (
		<Text
			variant="titleMedium"
			style={{
				color: deltaColorByDirection[delta.direction],
				fontWeight: "700",
				textAlign: "center",
			}}
		>
			{delta.label}
		</Text>
	);
};

const MiniDonut = ({
	label,
	percentage,
	color,
	emphasized,
}: {
	label: string;
	percentage: number;
	color: string;
	emphasized?: boolean;
}) => {
	const theme = useAppTheme();
	return (
		<Box alignItems="center" style={{ gap: 8 }}>
			<ScoreDonut
				percentage={percentage}
				color={color}
				radius={56}
				innerRadius={44}
			>
				<Text
					variant="titleMedium"
					style={{
						color: emphasized
							? theme.colors.onSurface
							: theme.colors.onSurfaceVariant,
						fontWeight: "700",
					}}
				>
					{`${percentage}%`}
				</Text>
			</ScoreDonut>
			<Text
				variant="labelMedium"
				style={{ color: theme.colors.onSurfaceVariant }}
			>
				{label}
			</Text>
		</Box>
	);
};

export const ProethicaPretestComparisonCard = ({
	prePercentage,
	postPercentage,
}: Props) => {
	const theme = useAppTheme();
	const delta = buildProethicaScoreDelta(prePercentage, postPercentage);

	return (
		<CardContainer
			title="Progress since pre-test"
			subtitle="How far you've come"
			icon="chart-timeline-variant"
			style={{ height: "100%" }}
			contentStyle={{ flex: 1 }}
		>
			<Box
				flex={1}
				paddingHorizontal={24}
				paddingVertical={40}
				justifyContent="center"
				style={{ gap: 24 }}
			>
				<Box
					flex={1}
					flexDirection="row"
					alignItems="center"
					justifyContent="center"
					style={{ gap: 48 }}
				>
					<MiniDonut
						label="Pre-test"
						percentage={prePercentage}
						color={theme.colors.inversePrimary}
					/>
					<Icon
						name="arrow-right"
						size={20}
						color={theme.colors.onSurfaceVariant}
					/>
					<MiniDonut
						label="Post-test"
						percentage={postPercentage}
						color={theme.colors.primary}
						emphasized
					/>
				</Box>
				<DeltaLine delta={delta} />
			</Box>
		</CardContainer>
	);
};
